import { useRef, useState, type ReactNode } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { DateField, Modal } from "@fawkes/ui";
import type { LucideIcon } from "lucide-react";
import {
  Ban,
  CalendarDays,
  CalendarPlus,
  CalendarRange,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Euro,
  FlaskConical,
  House,
  MoreHorizontal,
  PawPrint,
  Pencil,
  PieChart,
  RotateCcw,
  StickyNote,
  Sun,
  Ticket,
  Trash2,
} from "lucide-react";

import { formatEuro } from "../../i18n/format";
import type {
  Afrekenwijze,
  ContractStatus,
  OpvangDag,
  OpvangDier,
  PensionDagopvangOverzicht,
  PensionDier,
  Weekdag,
} from "../pensionKlantTypes";
import { DAG_TOON } from "./BoekingTabs";
import { tijd } from "./boekingen";
import { LEEG, datumMetJaar, periode, type Context } from "./hulp";
import { Rondje } from "./Tegels";
import { DierLink, Laadstaat, Leeg, Pil, Sectie, Verder, type PilToon } from "./Sectie";
import "./DagopvangTab.css";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

/** Zoveel maanden staan er naast elkaar in de kalender. */
const MAANDEN = 3;
const WEEK: Weekdag[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const WIJZEN: Afrekenwijze[] = ["RITTENKAART", "PERIODE", "MAANDCONTRACT"];

const CONTRACT_TOON: Record<ContractStatus, PilToon> = {
  LOPEND: "groen",
  TOEKOMSTIG: "accent",
  OPGEZEGD: "amber",
  AFGELOPEN: "grijs",
  GEANNULEERD: "rood",
};

/** Hoe een dag in de kalender en de lijst gekleurd wordt. */
type KalSoort = "vast" | "extra" | "afwezig" | "proefdag" | "feestdag" | "verwacht";

type Venster =
  | { soort: "dag"; dag: OpvangDag | null; datum: string }
  | { soort: "afmelden"; dag: OpvangDag }
  | { soort: "verwijderen"; dag: OpvangDag }
  | { soort: "vasteDagen" }
  | { soort: "afrekening"; wijze: Afrekenwijze }
  | { soort: "rittenkaart" };

// ── Datums ───────────────────────────────────────────────────────────────────────────────────────────

function utc(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`);
}

function isoVan(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** De eerste van de maand, `n` maanden verder. */
export function plusMaanden(maand: string, n: number): string {
  const d = utc(maand);
  return isoVan(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)));
}

export function laatsteVanMaand(maand: string): string {
  const d = utc(maand);
  return isoVan(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
}

function weekdagVan(datum: string): Weekdag {
  return WEEK[(utc(datum).getUTCDay() + 6) % 7];
}

/** 1 januari 2024 was een maandag; daarvandaan de namen van de weekdagen in de taal van de gebruiker. */
function weekdagKort(i: number, locale: string): string {
  const naam = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" })
    .format(new Date(Date.UTC(2024, 0, 1 + i)))
    .replace(".", "")
    .slice(0, 2);
  return naam.charAt(0).toUpperCase() + naam.slice(1);
}

function weekdagLang(i: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2024, 0, 1 + i)));
}

function maandNaam(maand: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(utc(maand));
}

/** "ma 8 dec 2025". */
function weekdagDatum(iso: string | null, locale: string): string {
  if (!iso) return LEEG;
  const dag = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(utc(iso)).replace(".", "");
  return `${dag} ${datumMetJaar(iso, locale).replace(/^0/, "")}`;
}

/** "maandag en donderdag". */
function dagenLang(dagen: Weekdag[], ctx: Context): string {
  const namen = WEEK.flatMap((w, i) => (dagen.includes(w) ? [weekdagLang(i, ctx.locale)] : []));
  if (namen.length <= 1) return namen.join("");
  return `${namen.slice(0, -1).join(", ")} ${ctx.t("pensionKlant.opvang.en")} ${namen[namen.length - 1]}`;
}

// ── Afleidingen ──────────────────────────────────────────────────────────────────────────────────────

/** Extra is een dag als niet elk gekozen dier die weekdag als vaste dag heeft; zo rekent de api ook. */
export function isExtraDag(dieren: OpvangDier[], ids: number[], datum: string): boolean {
  const weekdag = weekdagVan(datum);
  return ids.some((id) => !dieren.find((d) => d.id === id)?.dagen.includes(weekdag));
}

function soortVan(d: OpvangDag): KalSoort {
  if (d.status === "GEANNULEERD") return "afwezig";
  if (d.soort === "PROEFDAG") return "proefdag";
  return d.soort === "EXTRA_DAG" ? "extra" : "vast";
}

/** De dieren op het contract eerst, dan de andere dieren van de klant. */
function kandidaten(opvang: PensionDagopvangOverzicht, dieren: PensionDier[]): { id: number; naam: string | null }[] {
  const erop = new Set(opvang.dieren.map((d) => d.id));
  return [
    ...opvang.dieren.map((d) => ({ id: d.id, naam: d.naam })),
    ...dieren.filter((d) => !erop.has(d.id)).map((d) => ({ id: d.id, naam: d.naam })),
  ];
}

/** Wie er op een nieuwe dag komt: de dieren met die vaste dag, anders alle dieren van het contract. */
function standaardDieren(opvang: PensionDagopvangOverzicht, datum: string): number[] {
  const weekdag = weekdagVan(datum);
  const vast = opvang.dieren.filter((d) => d.dagen.includes(weekdag)).map((d) => d.id);
  return vast.length > 0 ? vast : opvang.dieren.map((d) => d.id);
}

function afrekenTekst(d: OpvangDag, { t, locale }: Context): string {
  switch (d.afrekening) {
    case "INBEGREPEN":
      return t("pensionKlant.opvang.afrekening.INBEGREPEN");
    case "RITTENKAART":
      return d.dieren.length === 1
        ? t("pensionKlant.opvang.afrekening.eenRit")
        : t("pensionKlant.opvang.afrekening.ritten", { args: [d.dieren.length] });
    case "DAGPRIJS":
      return d.bedrag == null ? t("pensionKlant.opvang.afrekening.DAGPRIJS") : formatEuro(d.bedrag, locale);
    case "PROEFDAG":
      return t("pensionKlant.opvang.afrekening.PROEFDAG");
    default:
      return LEEG;
  }
}

function foutSleutel(fout: unknown): string {
  const status = (fout as { statusCode?: number } | null)?.statusCode;
  if (status === 409) return "pensionKlant.opvang.fout.conflict";
  if (status === 422) return "pensionKlant.opvang.fout.geenContract";
  if (status === 400) return "pensionKlant.opvang.fout.ongeldig";
  return "pensionKlant.opvang.fout.algemeen";
}

// ── Het tabblad ──────────────────────────────────────────────────────────────────────────────────────

/**
 * Het dagopvangprofiel van de klant: bovenaan het contract, links de kalender over drie maanden, de geplande dagen
 * en de dieren, rechts hoe er wordt afgerekend (rittenkaart, per periode of maandcontract), de huidige maand en de
 * eerstvolgende opvangdag. Plannen, afmelden en de afrekenwijze wijzigen gaan in een venster.
 *
 * <p>Dagopvang staat voor elke klant open. Een klant zonder contract kreeg hier "maakt geen gebruik van dagopvang"
 * te zien en kwam geen stap verder; nu staat er een knop om een dag in te plannen. Die eerste dag begint de
 * dagopvang: de api legt er een lopend contract zonder einddatum bij neer, en daarna staat de kalender er.
 */
export function DagopvangTab({
  klantId,
  dieren,
  fotos,
  ctx,
  onKiesDier,
}: {
  klantId: string;
  dieren: PensionDier[];
  fotos: Record<string, string | undefined>;
  ctx: Context;
  onKiesDier: (id: number) => void;
}) {
  const { t, locale, vandaag } = ctx;
  const dezeMaand = `${vandaag.slice(0, 7)}-01`;
  const [begin, setBegin] = useState(dezeMaand);
  const eind = laatsteVanMaand(plusMaanden(begin, MAANDEN - 1));
  const basis = `pension/klanten/${klantId}/dagopvang`;
  const { data, isError, refetch } = useCustom<PensionDagopvangOverzicht>({
    url: `${basis}/overzicht?van=${begin}&tot=${eind}`,
    method: "get",
  });
  // Bij het bladeren blijft de vorige periode staan tot de nieuwe er is, anders knippert het hele blad.
  const vorige = useRef<PensionDagopvangOverzicht | undefined>(undefined);
  if (data?.data) vorige.current = data.data;
  const opvang = data?.data ?? vorige.current;

  const [filter, setFilter] = useState<number | null>(null);
  const [maandKeuze, setMaandKeuze] = useState<string>(dezeMaand);
  const [venster, setVenster] = useState<Venster | null>(null);
  const [menu, setMenu] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);
  const { mutateAsync } = useCustomMutation();

  if (!opvang) {
    return <Laadstaat laden={!isError} fout={isError} onOpnieuw={() => refetch()} ctx={ctx} />;
  }
  const c = opvang.contract;
  if (!c) {
    // Nog geen dagopvang: de eerste ingeplande dag begint hem. Het venster is hetzelfde als altijd.
    return (
      <>
        <div className="pk-paneel pk-paneel--leeg pk-ov__begin">
          <p>{t("pensionKlant.dagopvang.geen")}</p>
          <button type="button" className="pk-knop pk-knop--hoofd" onClick={() => setVenster({ soort: "dag", dag: null, datum: vandaag })}>
            <CalendarPlus size={15} aria-hidden="true" /> {t("pensionKlant.opvang.dag.plannen")}
          </button>
        </div>
        {venster?.soort === "dag" && (
          <DagVenster
            basis={basis}
            opvang={opvang}
            dieren={dieren}
            fotos={fotos}
            dag={null}
            datum={venster.datum}
            ctx={ctx}
            onSluit={() => setVenster(null)}
            onKlaar={() => {
              setVenster(null);
              void refetch();
            }}
          />
        )}
      </>
    );
  }

  const a = opvang.afrekening;
  const maanden = Array.from({ length: MAANDEN }, (_, i) => plusMaanden(begin, i));
  const gekozenMaand = maandKeuze === "alle" || maanden.includes(maandKeuze) ? maandKeuze : maanden[0];
  const tabelDagen = opvang.dagen.filter(
    (d) =>
      (gekozenMaand === "alle" || d.datum?.startsWith(gekozenMaand.slice(0, 7))) &&
      (filter == null || d.dieren.some((x) => x.id === filter)),
  );
  const looptijd = c.van
    ? c.onbeperkt || !c.tot
      ? t("pensionKlant.dagopvang.vanafOnbeperkt", { args: [datumMetJaar(c.van, locale)] })
      : periode(c.van, c.tot, locale)
    : t("pensionKlant.opvang.doorlopend");
  const sluit = () => setVenster(null);
  const klaar = () => {
    setVenster(null);
    setMelding(null);
    refetch();
  };
  const terugzetten = async (dag: OpvangDag) => {
    setMelding(null);
    try {
      await mutateAsync({
        url: `${basis}/dagen/${dag.id}/terugzetten`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      });
      refetch();
    } catch (fout) {
      setMelding(t(foutSleutel(fout)));
    }
  };
  const planDatum = opvang.verwacht[0]?.datum ?? vandaag;

  return (
    <div className="pk-ov">
      <section className="pk-tegel pk-ov__kop" aria-label={t("pensionKlant.opvang.titel")}>
        <Rondje icoon={Sun} tint="amber" grootte="kop" />
        <div className="pk-ov__titel">
          <div className="pk-ov__titelregel">
            <h3>{t("pensionKlant.opvang.titel")}</h3>
            <Pil toon={CONTRACT_TOON[c.status]}>{t(`pensionKlant.contractstatus.${c.status}`)}</Pil>
            {a?.afrekenwijze === "MAANDCONTRACT" && a.maandcontract.vasteDagen.length > 0 && (
              <Pil toon="accent">{t("pensionKlant.opvang.vasteKlant")}</Pil>
            )}
            <Pil toon="grijs">
              {opvang.dieren.length === 1
                ? t("pensionKlant.opvang.eenDier")
                : t("pensionKlant.opvang.dieren.aantal", { args: [opvang.dieren.length] })}
            </Pil>
          </div>
          <p className="pk-ov__sub">
            {t("pensionKlant.dagopvang.contract", { args: [c.nummer ?? c.id] })} · {looptijd}
          </p>
        </div>
        <div className="pk-ov__knoppen">
          <button type="button" className="pk-knop" onClick={() => setVenster({ soort: "vasteDagen" })}>
            <Pencil size={15} aria-hidden="true" />
            {t("pensionKlant.opvang.bewerken")}
          </button>
          <button
            type="button"
            className="pk-knop pk-knop--hoofd"
            onClick={() => setVenster({ soort: "dag", dag: null, datum: planDatum })}
          >
            <CalendarPlus size={15} aria-hidden="true" />
            {t("pensionKlant.opvang.dagPlannen")}
          </button>
          <div className="pk-ov__meer">
            <button
              type="button"
              className="pk-knop pk-ov__meerknop"
              aria-label={t("pensionKlant.opvang.meer")}
              aria-haspopup="menu"
              aria-expanded={menu}
              onClick={() => setMenu((x) => !x)}
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </button>
            {menu && (
              <div className="pk-ov__menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenu(false);
                    setVenster({ soort: "rittenkaart" });
                  }}
                >
                  <Ticket size={15} aria-hidden="true" />
                  {t("pensionKlant.opvang.rittenkaartVerkopen")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenu(false);
                    setVenster({ soort: "afrekening", wijze: a?.afrekenwijze ?? "PERIODE" });
                  }}
                >
                  <Euro size={15} aria-hidden="true" />
                  {t("pensionKlant.opvang.afrekenwijzeWijzigen")}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {melding && (
        <p className="pk-voetnoot" data-soort="fout" role="alert">
          {melding}
        </p>
      )}

      <div className="pk-ov__raster">
        <div className="pk-blad__kolom">
          <Sectie
            titel={t("pensionKlant.opvang.kalender")}
            icoon={CalendarDays}
            tint="groen"
            actie={
              <div className="pk-ov__bladeren">
                <button
                  type="button"
                  className="pk-knop pk-knop--klein"
                  aria-label={t("pensionKlant.opvang.eerder")}
                  onClick={() => setBegin(plusMaanden(begin, -1))}
                >
                  <ChevronLeft size={15} aria-hidden="true" />
                </button>
                <button type="button" className="pk-knop pk-knop--klein" onClick={() => setBegin(dezeMaand)}>
                  {t("pensionKlant.opvang.vandaag")}
                </button>
                <button
                  type="button"
                  className="pk-knop pk-knop--klein"
                  aria-label={t("pensionKlant.opvang.later")}
                  onClick={() => setBegin(plusMaanden(begin, 1))}
                >
                  <ChevronRight size={15} aria-hidden="true" />
                </button>
              </div>
            }
          >
            {opvang.dieren.length > 1 && (
              <div className="pk-ov__filter" role="group" aria-label={t("pensionKlant.opvang.filter")}>
                <button
                  type="button"
                  className="pk-ov__filterknop"
                  aria-pressed={filter == null}
                  onClick={() => setFilter(null)}
                >
                  {t("pensionKlant.opvang.alleDieren")}
                </button>
                {opvang.dieren.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className="pk-ov__filterknop"
                    aria-pressed={filter === d.id}
                    onClick={() => setFilter(d.id)}
                  >
                    <PawPrint size={12} aria-hidden="true" />
                    {d.naam ?? LEEG}
                  </button>
                ))}
              </div>
            )}
            <Kalender
              opvang={opvang}
              fotos={fotos}
              maanden={maanden}
              filter={filter}
              ctx={ctx}
              onDag={(datum, dag) => {
                if (dag?.gefactureerd) return;
                setVenster({ soort: "dag", dag, datum });
              }}
            />
            <Legenda ctx={ctx} />
          </Sectie>

          <Sectie
            titel={t("pensionKlant.opvang.gepland")}
            icoon={ClipboardList}
            tint="accent"
            actie={
              <select
                className="pk-ov__maandkeuze"
                aria-label={t("pensionKlant.opvang.maandKeuze")}
                value={gekozenMaand}
                onChange={(e) => setMaandKeuze(e.target.value)}
              >
                {maanden.map((m) => (
                  <option key={m} value={m}>
                    {maandNaam(m, locale)}
                  </option>
                ))}
                <option value="alle">{t("pensionKlant.opvang.alleMaanden")}</option>
              </select>
            }
          >
            <DagenTabel
              dagen={tabelDagen}
              fotos={fotos}
              ctx={ctx}
              onKiesDier={onKiesDier}
              onBewerk={(dag) => setVenster({ soort: "dag", dag, datum: dag.datum ?? vandaag })}
              onAfmelden={(dag) => setVenster({ soort: "afmelden", dag })}
              onTerugzetten={(dag) => void terugzetten(dag)}
              onVerwijder={(dag) => setVenster({ soort: "verwijderen", dag })}
            />
            <Verder onClick={() => setMaandKeuze(gekozenMaand === "alle" ? maanden[0] : "alle")}>
              {gekozenMaand === "alle" ? t("pensionKlant.opvang.minderBekijken") : t("pensionKlant.opvang.alleBekijken")}
            </Verder>
          </Sectie>
        </div>

        <div className="pk-blad__kolom">
          <Sectie titel={t("pensionKlant.opvang.afrekenwijze")} icoon={Euro} tint="groen">
            {a && (
              <AfrekenKeuze
                opvang={opvang}
                ctx={ctx}
                onKies={(wijze) => setVenster({ soort: "afrekening", wijze })}
                onRittenkaart={() => setVenster({ soort: "rittenkaart" })}
              />
            )}
          </Sectie>
          <Sectie titel={t("pensionKlant.opvang.samenvatting")} icoon={PieChart} tint="paars">
            <Samenvatting opvang={opvang} ctx={ctx} />
          </Sectie>
          <Sectie titel={t("pensionKlant.opvang.volgende")} icoon={Clock} tint="teal">
            <Volgende opvang={opvang} fotos={fotos} ctx={ctx} onKiesDier={onKiesDier} />
          </Sectie>
        </div>
      </div>

      {venster?.soort === "dag" && (
        <DagVenster
          basis={basis}
          opvang={opvang}
          dieren={dieren}
          fotos={fotos}
          dag={venster.dag}
          datum={venster.datum}
          ctx={ctx}
          onSluit={sluit}
          onKlaar={klaar}
        />
      )}
      {venster?.soort === "afmelden" && (
        <AfmeldVenster basis={basis} dag={venster.dag} ctx={ctx} onSluit={sluit} onKlaar={klaar} />
      )}
      {venster?.soort === "verwijderen" && (
        <VerwijderVenster basis={basis} dag={venster.dag} ctx={ctx} onSluit={sluit} onKlaar={klaar} />
      )}
      {venster?.soort === "vasteDagen" && (
        <VasteDagenVenster basis={basis} opvang={opvang} dieren={dieren} ctx={ctx} onSluit={sluit} onKlaar={klaar} />
      )}
      {venster?.soort === "afrekening" && (
        <AfrekeningVenster
          basis={basis}
          opvang={opvang}
          wijze={venster.wijze}
          ctx={ctx}
          onSluit={sluit}
          onKlaar={klaar}
        />
      )}
      {venster?.soort === "rittenkaart" && (
        <RittenkaartVenster basis={basis} opvang={opvang} ctx={ctx} onSluit={sluit} onKlaar={klaar} />
      )}
    </div>
  );
}

// ── Kalender ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Zoveel dieren passen er in een dagvakje. Zijn het er meer, dan is er ook nog een "+2" nodig en past er
 * dus één foto minder: twee foto's, of anders één foto met het aantal erachter.
 */
const KAL_FOTOS = 2;

function kalenderDieren<T>(dieren: T[]): { toon: T[]; meer: number } {
  if (dieren.length <= KAL_FOTOS) return { toon: dieren, meer: 0 };
  return { toon: dieren.slice(0, KAL_FOTOS - 1), meer: dieren.length - (KAL_FOTOS - 1) };
}

function Kalender({
  opvang,
  maanden,
  filter,
  fotos,
  ctx,
  onDag,
}: {
  opvang: PensionDagopvangOverzicht;
  maanden: string[];
  filter: number | null;
  fotos: Record<string, string | undefined>;
  ctx: Context;
  onDag: (datum: string, dag: OpvangDag | null) => void;
}) {
  const { t, locale, vandaag } = ctx;
  const past = (lijst: { id: number }[]) => filter == null || lijst.some((x) => x.id === filter);
  // Een afgemelde dag wijkt voor een nieuwe op dezelfde datum.
  const dagOp = new Map<string, OpvangDag>();
  for (const d of opvang.dagen) {
    if (!d.datum || !past(d.dieren)) continue;
    const sleutel = d.datum.slice(0, 10);
    const er = dagOp.get(sleutel);
    if (!er || er.status === "GEANNULEERD") dagOp.set(sleutel, d);
  }
  const verwacht = new Map(opvang.verwacht.filter((v) => past(v.dieren)).map((v) => [v.datum, v]));
  const feest = new Map(opvang.feestdagen.map((f) => [f.datum, f.naam]));

  return (
    <div className="pk-kal">
      {maanden.map((maand) => {
        const leeg = (utc(maand).getUTCDay() + 6) % 7;
        const aantal = utc(laatsteVanMaand(maand)).getUTCDate();
        return (
          <div key={maand} className="pk-kal__maand">
            <h4>{maandNaam(maand, locale)}</h4>
            <div className="pk-kal__raster" role="group" aria-label={maandNaam(maand, locale)}>
              {WEEK.map((w, i) => (
                <span key={w} className="pk-kal__kopje" aria-hidden="true">
                  {weekdagKort(i, locale)}
                </span>
              ))}
              {Array.from({ length: leeg }, (_, i) => (
                <span key={`leeg${i}`} />
              ))}
              {Array.from({ length: aantal }, (_, i) => {
                const datum = `${maand.slice(0, 8)}${String(i + 1).padStart(2, "0")}`;
                const dag = dagOp.get(datum);
                const feestdag = feest.get(datum);
                const v = verwacht.get(datum);
                const soort: KalSoort | undefined = dag ? soortVan(dag) : feestdag ? "feestdag" : v ? "verwacht" : undefined;
                const opDeDag = dag ? (dag.status === "GEANNULEERD" ? [] : dag.dieren) : (v?.dieren ?? []);
                const namen = opDeDag.map((x) => x.naam).filter(Boolean);
                const { toon, meer } = kalenderDieren(opDeDag);
                const label = [
                  weekdagDatum(datum, locale),
                  soort ? t(`pensionKlant.opvang.soort.${soort}`) : null,
                  feestdag,
                  namen.join(", "),
                ]
                  .filter(Boolean)
                  .join(" — ");
                return (
                  <button
                    key={datum}
                    type="button"
                    className="pk-kal__dag"
                    data-soort={soort}
                    data-vandaag={datum === vandaag || undefined}
                    aria-label={label}
                    title={label}
                    disabled={!dag && !!feestdag}
                    onClick={() => onDag(datum, dag ?? null)}
                  >
                    <span className="pk-kal__nummer">{i + 1}</span>
                    {opDeDag.length > 0 && (
                      <span className="pk-kal__dieren" aria-hidden="true">
                        {toon.map((x) => (
                          <DierStip key={x.id} naam={x.naam} foto={fotos[String(x.id)]} />
                        ))}
                        {meer > 0 && <span className="pk-kal__meer">+{meer}</span>}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Het dier in een dagvakje: zijn foto, of anders de eerste letter van zijn naam. */
function DierStip({ naam, foto }: { naam: string | null; foto?: string }) {
  if (foto) return <img className="pk-kal__dier" src={foto} alt="" />;
  return <span className="pk-kal__dier pk-kal__dier--leeg">{(naam ?? "?").slice(0, 1).toUpperCase()}</span>;
}

function Legenda({ ctx }: { ctx: Context }) {
  const soorten: KalSoort[] = ["vast", "extra", "afwezig", "proefdag", "feestdag", "verwacht"];
  return (
    <ul className="pk-kal__legenda">
      {soorten.map((s) => (
        <li key={s}>
          <span className="pk-kal__staal" data-soort={s} aria-hidden="true" />
          {ctx.t(`pensionKlant.opvang.soort.${s}`)}
        </li>
      ))}
      <li>
        <span className="pk-kal__dieren pk-kal__dieren--los" aria-hidden="true">
          <span className="pk-kal__dier pk-kal__dier--leeg" />
          <span className="pk-kal__dier pk-kal__dier--leeg" />
        </span>
        {ctx.t("pensionKlant.opvang.legenda.dieren")}
      </li>
    </ul>
  );
}

// ── Geplande dagen ───────────────────────────────────────────────────────────────────────────────────

function DagenTabel({
  dagen,
  fotos,
  ctx,
  onKiesDier,
  onBewerk,
  onAfmelden,
  onTerugzetten,
  onVerwijder,
}: {
  dagen: OpvangDag[];
  fotos: Record<string, string | undefined>;
  ctx: Context;
  onKiesDier: (id: number) => void;
  onBewerk: (dag: OpvangDag) => void;
  onAfmelden: (dag: OpvangDag) => void;
  onTerugzetten: (dag: OpvangDag) => void;
  onVerwijder: (dag: OpvangDag) => void;
}) {
  const { t, locale } = ctx;
  if (dagen.length === 0) return <Leeg>{t("pensionKlant.opvang.geenDagen")}</Leeg>;
  return (
    <div className="pk-ov__tabelvak">
      <IndeelbareTabel
        tabelKey="pension-dagopvang-dagen"
        columns={[
          {
            key: "datum", header: t("pensionKlant.opvang.kolom.datum"),
            cell: (d) => (
              <>
                <span className="pk-ov__datum">{weekdagDatum(d.datum, locale)}</span>
                {d.van && d.tot && (
                  <span className="pk-ov__tijd">
                    {tijd(d.van)}&ndash;{tijd(d.tot)}
                  </span>
                )}
              </>
            ),
          },
          {
            key: "dieren", header: t("pensionKlant.opvang.kolom.dieren"),
            cell: (d) => (
              <span className="pk-dierlinks">
                {d.dieren.map((x) => (
                  <DierLink key={x.id} id={x.id} naam={x.naam} foto={fotos[String(x.id)]} onKiesDier={onKiesDier} />
                ))}
              </span>
            ),
          },
          {
            key: "type", header: t("pensionKlant.opvang.kolom.type"),
            cell: (d) => (
              <span className="pk-ov__soort" data-soort={soortVan(d)}>
                {t(`pensionKlant.opvang.type.${d.soort}`)}
              </span>
            ),
          },
          {
            key: "status", header: t("pensionKlant.opvang.kolom.status"),
            cell: (d) => <Pil toon={DAG_TOON[d.status]}>{t(`pensionKlant.dagstatus.${d.status}`)}</Pil>,
          },
          {
            key: "afrekening", header: t("pensionKlant.opvang.kolom.afrekening"),
            cell: (d) => (
              <span className="pk-ov__afrekening">
                {afrekenTekst(d, ctx)}
                {d.gefactureerd && <span className="pk-chip">{t("pensionKlant.opvang.gefactureerd")}</span>}
              </span>
            ),
          },
          {
            key: "opmerking", header: t("pensionKlant.opvang.kolom.opmerking"),
            cell: (d) => (
              <span className="pk-ov__opmerking">
                {(d.status === "GEANNULEERD" && d.afwezigReden) || d.opmerking || LEEG}
              </span>
            ),
          },
          {
            key: "acties", header: "", vast: true,
            cell: (d) => {
              const datum = weekdagDatum(d.datum, locale);
              const afgemeld = d.status === "GEANNULEERD";
              const gekomen = d.status === "AANWEZIG" || d.status === "OPGEHAALD";
              return (
                <div className="pk-ov__acties">
                  {!d.gefactureerd && (
                    <IcoonKnop
                      label={t("pensionKlant.opvang.actie.bewerk", { args: [datum] })}
                      icoon={Pencil}
                      onClick={() => onBewerk(d)}
                    />
                  )}
                  {!d.gefactureerd && !gekomen && afgemeld && (
                    <IcoonKnop
                      label={t("pensionKlant.opvang.actie.terugzetten", { args: [datum] })}
                      icoon={RotateCcw}
                      onClick={() => onTerugzetten(d)}
                    />
                  )}
                  {!d.gefactureerd && !gekomen && !afgemeld && (
                    <IcoonKnop
                      label={t("pensionKlant.opvang.actie.afmelden", { args: [datum] })}
                      icoon={Ban}
                      onClick={() => onAfmelden(d)}
                    />
                  )}
                  {!d.gefactureerd && !gekomen && (
                    <IcoonKnop
                      label={t("pensionKlant.opvang.actie.verwijder", { args: [datum] })}
                      icoon={Trash2}
                      gevaar
                      onClick={() => onVerwijder(d)}
                    />
                  )}
                </div>
              );
            },
          },
        ]}
        rows={dagen}
        getRowKey={(d) => String(d.id)}
        // Een afgemelde dag staat grijs; dat hing aan een data-attribuut op de rij, en een tabel
        // geeft een rij alleen een klasse mee.
        rowClassName={(d) => (d.status === "GEANNULEERD" ? "pk-ov__rij--afgemeld" : undefined)}
        empty=""
      />
    </div>
  );
}

function IcoonKnop({
  label,
  icoon: Icoon,
  gevaar,
  onClick,
}: {
  label: string;
  icoon: LucideIcon;
  gevaar?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="pk-ov__icoonknop"
      data-gevaar={gevaar || undefined}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <Icoon size={15} aria-hidden="true" />
    </button>
  );
}

// ── Rechterkolom ─────────────────────────────────────────────────────────────────────────────────────

function AfrekenKeuze({
  opvang,
  ctx,
  onKies,
  onRittenkaart,
}: {
  opvang: PensionDagopvangOverzicht;
  ctx: Context;
  onKies: (wijze: Afrekenwijze) => void;
  onRittenkaart: () => void;
}) {
  const { t, locale } = ctx;
  const a = opvang.afrekening!;
  const { rittenkaart: r, periode: p, maandcontract: m } = a;
  const regels: Record<Afrekenwijze, ReactNode> = {
    RITTENKAART: (
      <>
        <span>{t("pensionKlant.opvang.rit.stand", { args: [r.gekocht, r.resterend] })}</span>
        <span>{t("pensionKlant.opvang.rit.perDier")}</span>
        {r.verlooptOp && (
          <span>{t("pensionKlant.opvang.rit.verloopt", { args: [datumMetJaar(r.verlooptOp, locale)] })}</span>
        )}
      </>
    ),
    PERIODE: (
      <span>
        {p?.dagprijs != null
          ? t("pensionKlant.opvang.periode.stand", { args: [formatEuro(p.dagprijs, locale)] })
          : t("pensionKlant.opvang.periode.geenPrijs")}
      </span>
    ),
    MAANDCONTRACT: (
      <>
        <span>
          {m.vasteDagen.length > 0
            ? t("pensionKlant.opvang.maand.vast", { args: [dagenLang(m.vasteDagen, ctx)] })
            : t("pensionKlant.opvang.maand.geenVast")}
        </span>
        <span>{t("pensionKlant.opvang.maand.inbegrepen", { args: [m.inbegrepenPerMaand] })}</span>
        <span>{t(`pensionKlant.opvang.maand.extra.${m.extraDagen}`)}</span>
        {m.abonnement && (
          <span>
            {m.prijsPerMaand != null
              ? t("pensionKlant.opvang.maand.abonnement", { args: [m.abonnement, formatEuro(m.prijsPerMaand, locale)] })
              : m.abonnement}
          </span>
        )}
      </>
    ),
  };
  return (
    <div className="pk-ov__afreken">
      <div className="pk-ov__wijzen" role="radiogroup" aria-label={t("pensionKlant.opvang.afrekenwijze")}>
        {WIJZEN.map((w) => (
          <button
            key={w}
            type="button"
            role="radio"
            aria-checked={a.afrekenwijze === w}
            className="pk-ov__wijze"
            onClick={() => onKies(w)}
          >
            <span className="pk-ov__bol" aria-hidden="true" />
            <span className="pk-ov__wijzetekst">
              <strong>{t(`pensionKlant.opvang.wijze.${w}`)}</strong>
              {regels[w]}
            </span>
          </button>
        ))}
      </div>
      {!a.ingesteld && <p className="pk-voetnoot">{t("pensionKlant.opvang.afgeleid")}</p>}
      <button type="button" className="pk-knop pk-knop--klein" onClick={onRittenkaart}>
        <Ticket size={14} aria-hidden="true" />
        {t("pensionKlant.opvang.rittenkaartVerkopen")}
      </button>
    </div>
  );
}

function Samenvatting({ opvang, ctx }: { opvang: PensionDagopvangOverzicht; ctx: Context }) {
  const { t, locale } = ctx;
  const s = opvang.samenvatting;
  const cijfers: [string, number][] = [
    ["inbegrepen", s.inbegrepen],
    ["gebruikt", s.gebruikt],
    ["extra", s.extra],
    ["beschikbaar", s.beschikbaar],
  ];
  return (
    <>
      <p className="pk-ov__maand">{maandNaam(s.maand, locale)}</p>
      <dl className="pk-ov__cijfers">
        {cijfers.map(([sleutel, n]) => (
          <div key={sleutel} className="pk-ov__cijfer" data-soort={sleutel}>
            <dt>{t(`pensionKlant.opvang.som.${sleutel}`)}</dt>
            <dd>{n}</dd>
          </div>
        ))}
      </dl>
      <div className="pk-ov__open">
        <span>{t("pensionKlant.opvang.som.openstaand")}</span>
        <strong>{formatEuro(s.openstaand, locale)}</strong>
      </div>
    </>
  );
}

function Volgende({
  opvang,
  fotos,
  ctx,
  onKiesDier,
}: {
  opvang: PensionDagopvangOverzicht;
  fotos: Record<string, string | undefined>;
  ctx: Context;
  onKiesDier: (id: number) => void;
}) {
  const { t, locale } = ctx;
  const d = opvang.volgende;
  if (!d?.datum) return <Leeg>{t("pensionKlant.opvang.geenVolgende")}</Leeg>;
  const datum = utc(d.datum);
  return (
    <div className="pk-ov__volgende">
      <span className="pk-ov__blok" aria-hidden="true">
        <span>
          {new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(datum).replace(".", "").toUpperCase()}
        </span>
        <strong>{datum.getUTCDate()}</strong>
        <span>{new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(datum).replace(".", "")}</span>
      </span>
      <div className="pk-ov__volgendetekst">
        <strong>{weekdagDatum(d.datum, locale)}</strong>
        <span className="pk-dierlinks">
          {d.dieren.map((x) => (
            <DierLink key={x.id} id={x.id} naam={x.naam} foto={fotos[String(x.id)]} onKiesDier={onKiesDier} />
          ))}
        </span>
        <dl className="pk-ov__tijden">
          <div>
            <dt>{t("pensionKlant.opvang.brengen")}</dt>
            <dd>{tijd(d.van) ?? LEEG}</dd>
          </div>
          <div>
            <dt>{t("pensionKlant.opvang.halen")}</dt>
            <dd>{tijd(d.tot) ?? LEEG}</dd>
          </div>
        </dl>
        <span className="pk-ov__soort" data-soort={soortVan(d)}>
          {t(`pensionKlant.opvang.type.${d.soort}`)}
        </span>
      </div>
    </div>
  );
}

// ── Vensters ─────────────────────────────────────────────────────────────────────────────────────────

interface VensterProps {
  basis: string;
  ctx: Context;
  onSluit: () => void;
  onKlaar: () => void;
}

/** Opslaan met de foutmelding erbij; gelukt sluit het venster en ververst het blad. */
function useOpslaan(ctx: Context, onKlaar: () => void) {
  const { mutateAsync, isLoading } = useCustomMutation();
  const [fout, setFout] = useState<string | null>(null);
  const doe = async (url: string, method: "post" | "put" | "delete", values: object = {}) => {
    setFout(null);
    try {
      await mutateAsync({ url, method, values, successNotification: false, errorNotification: false });
      onKlaar();
    } catch (e) {
      setFout(ctx.t(foutSleutel(e)));
    }
  };
  return { doe, bezig: isLoading, fout };
}

function Formulier({
  titel,
  knop,
  kan,
  bezig,
  fout,
  gevaar,
  ctx,
  onSluit,
  onOpslaan,
  children,
}: {
  titel: string;
  knop: string;
  kan: boolean;
  bezig: boolean;
  fout: string | null;
  gevaar?: boolean;
  ctx: Context;
  onSluit: () => void;
  onOpslaan: () => void;
  children: ReactNode;
}) {
  return (
    <Modal open title={titel} onClose={onSluit}>
      <form
        className="pk pk-ov__form"
        aria-label={titel}
        onSubmit={(e) => {
          e.preventDefault();
          if (kan && !bezig) onOpslaan();
        }}
      >
        {children}
        {fout && (
          <p className="pk-voetnoot" data-soort="fout" role="alert">
            {fout}
          </p>
        )}
        <div className="pk-ov__voet">
          <button type="button" className="pk-knop" onClick={onSluit}>
            {ctx.t("pensionKlant.opvang.annuleren")}
          </button>
          <button
            type="submit"
            className={gevaar ? "pk-knop pk-knop--gevaar" : "pk-knop pk-knop--hoofd"}
            disabled={!kan || bezig}
          >
            {knop}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** "vrijdag 25 september 2026" — voluit, want het is de kop van het venster. */
function dagVoluit(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })
    .format(utc(iso));
}

/** Regulier, extra dag of proefdag: je kiest er één. */
type Dagsoort = "regulier" | "extra" | "proefdag";

function DagVenster({
  basis,
  opvang,
  dieren,
  fotos,
  dag,
  datum,
  ctx,
  onSluit,
  onKlaar,
}: VensterProps & {
  opvang: PensionDagopvangOverzicht;
  dieren: PensionDier[];
  fotos: Record<string, string | undefined>;
  dag: OpvangDag | null;
  datum: string;
}) {
  const { t, locale } = ctx;
  // Bij de allereerste dag is er nog geen contract, en dus ook nog geen breng- en haaltijd om van uit te gaan.
  const c = opvang.contract;
  const [d, setD] = useState((dag?.datum ?? datum).slice(0, 10));
  const [anderDag, setAnderDag] = useState(false);
  const [ids, setIds] = useState<number[]>(() => (dag ? dag.dieren.map((x) => x.id) : standaardDieren(opvang, datum)));
  const [van, setVan] = useState(tijd(dag?.van ?? c?.brengen) ?? "08:00");
  const [tot, setTot] = useState(tijd(dag?.tot ?? c?.halen) ?? "18:00");
  const [opmerking, setOpmerking] = useState(dag?.opmerking ?? "");
  const { doe, bezig, fout } = useOpslaan(ctx, onKlaar);

  const geldig = /^\d{4}-\d{2}-\d{2}$/.test(d) && !Number.isNaN(utc(d).getTime());
  const afgeleid = geldig && isExtraDag(opvang.dieren, ids, d);
  // De soort volgt uit de dieren en de datum tot je hem zelf kiest; daarna blijft jouw keuze staan.
  const [gekozenSoort, setGekozenSoort] = useState<Dagsoort | null>(
    dag ? (dag.soort === "PROEFDAG" ? "proefdag" : dag.soort === "EXTRA_DAG" ? "extra" : "regulier") : null,
  );
  const soort: Dagsoort = gekozenSoort ?? (afgeleid ? "extra" : "regulier");
  const tijdFout = van !== "" && tot !== "" && tot <= van;
  const kan = geldig && ids.length > 0 && van !== "" && tot !== "" && !tijdFout;

  const soorten: { sleutel: Dagsoort; label: string; icoon: LucideIcon }[] = [
    { sleutel: "regulier", label: t("pensionKlant.opvang.dag.regulier"), icoon: House },
    { sleutel: "extra", label: t("pensionKlant.opvang.dag.extra"), icoon: CalendarPlus },
    { sleutel: "proefdag", label: t("pensionKlant.opvang.dag.proefdag"), icoon: FlaskConical },
  ];

  return (
    <Formulier
      titel={dag ? t("pensionKlant.opvang.dag.bewerkTitel2") : t("pensionKlant.opvang.dag.toevoegenTitel")}
      knop={dag ? t("pensionKlant.opvang.opslaan") : t("pensionKlant.opvang.dag.toevoegen")}
      kan={kan}
      bezig={bezig}
      fout={fout}
      ctx={ctx}
      onSluit={onSluit}
      onOpslaan={() =>
        void doe(dag ? `${basis}/dagen/${dag.id}` : `${basis}/dagen`, dag ? "put" : "post", {
          datum: d,
          van,
          tot,
          dierIds: ids,
          // Een proefdag laat de api zelf uitmaken of hij buiten de vaste dagen valt.
          extraDag: soort === "proefdag" ? null : soort === "extra",
          proefdag: soort === "proefdag",
          opmerking: opmerking.trim() === "" ? null : opmerking.trim(),
        })
      }
    >
      <p className="pk-dv__datum">
        <span>{geldig ? dagVoluit(d, locale) : LEEG}</span>
        <button type="button" className="pk-dv__anders" onClick={() => setAnderDag((x) => !x)}>
          {t("pensionKlant.opvang.dag.andereDag")}
        </button>
      </p>
      {anderDag && (
        <label className="pk-veld">
          <span>{t("pensionKlant.opvang.dag.datum")}</span>
          <DateField value={d} onChange={(e) => setD(e.target.value)} required />
        </label>
      )}

      <section className="pk-dv__vak" aria-label={t("pensionKlant.opvang.dag.dieren")}>
        <h4 className="pk-dv__kop">
          <Rondje icoon={PawPrint} tint="groen" grootte="kop" />
          {t("pensionKlant.opvang.dag.dieren")}
          <span className="pk-dv__telling">{t("pensionKlant.opvang.dag.geselecteerd", { args: [ids.length] })}</span>
        </h4>
        <div className="pk-dv__dieren">
          {kandidaten(opvang, dieren).map((k) => {
            const aan = ids.includes(k.id);
            const foto = fotos[String(k.id)];
            return (
              <button
                key={k.id}
                type="button"
                className="pk-dv__dier"
                aria-pressed={aan}
                onClick={() => setIds((x) => (aan ? x.filter((i) => i !== k.id) : [...x, k.id]))}
              >
                {foto ? (
                  <img className="pk-dv__dierfoto" src={foto} alt="" />
                ) : (
                  <span className="pk-dv__dierfoto pk-dv__dierfoto--leeg" aria-hidden="true">
                    <PawPrint size={20} />
                  </span>
                )}
                <span className="pk-dv__diernaam">{k.naam ?? LEEG}</span>
                {aan && (
                  <span className="pk-dv__vink" aria-hidden="true">
                    <Check size={11} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="pk-dv__vak" aria-label={t("pensionKlant.opvang.dag.tijd")}>
        <h4 className="pk-dv__kop">
          <Rondje icoon={Clock} tint="groen" grootte="kop" />
          {t("pensionKlant.opvang.dag.tijd")}
        </h4>
        <div className="pk-ov__twee">
          <label className="pk-veld">
            <span>{t("pensionKlant.opvang.brengen")}</span>
            <span className="pk-dv__tijd">
              <Clock size={14} aria-hidden="true" />
              <input type="time" value={van} onChange={(e) => setVan(e.target.value)} />
            </span>
          </label>
          <label className="pk-veld">
            <span>{t("pensionKlant.opvang.halen")}</span>
            <span className="pk-dv__tijd">
              <Clock size={14} aria-hidden="true" />
              <input type="time" value={tot} onChange={(e) => setTot(e.target.value)} />
            </span>
          </label>
        </div>
        {tijdFout && (
          <p className="pk-voetnoot" data-soort="fout">
            {t("pensionKlant.opvang.fout.tijden")}
          </p>
        )}
      </section>

      <section className="pk-dv__vak" aria-label={t("pensionKlant.opvang.dag.soort")}>
        <h4 className="pk-dv__kop">
          <Rondje icoon={Sun} tint="groen" grootte="kop" />
          {t("pensionKlant.opvang.dag.soort")}
        </h4>
        <div className="pk-dv__soorten" role="radiogroup" aria-label={t("pensionKlant.opvang.dag.soort")}>
          {soorten.map((x) => (
            <button
              key={x.sleutel}
              type="button"
              role="radio"
              aria-checked={soort === x.sleutel}
              className="pk-dv__soort"
              onClick={() => setGekozenSoort(x.sleutel)}
            >
              <x.icoon size={15} aria-hidden="true" />
              {x.label}
            </button>
          ))}
        </div>
        {gekozenSoort == null && ids.length > 0 && geldig && (
          <p className="pk-ov__uitleg">
            {afgeleid ? t("pensionKlant.opvang.dag.extraAfgeleid") : t("pensionKlant.opvang.dag.vastAfgeleid")}
          </p>
        )}
      </section>

      <section className="pk-dv__vak">
        <h4 className="pk-dv__kop">
          <Rondje icoon={StickyNote} tint="groen" grootte="kop" />
          {t("pensionKlant.opvang.dag.opmerking")}
        </h4>
        <textarea
          rows={3}
          maxLength={2000}
          aria-label={t("pensionKlant.opvang.dag.opmerking")}
          placeholder={t("pensionKlant.opvang.dag.opmerkingHint")}
          value={opmerking}
          onChange={(e) => setOpmerking(e.target.value)}
        />
      </section>
    </Formulier>
  );
}

function AfmeldVenster({ basis, dag, ctx, onSluit, onKlaar }: VensterProps & { dag: OpvangDag }) {
  const { t, locale } = ctx;
  const [reden, setReden] = useState("");
  const { doe, bezig, fout } = useOpslaan(ctx, onKlaar);
  return (
    <Formulier
      titel={t("pensionKlant.opvang.afmelden.titel", { args: [weekdagDatum(dag.datum, locale)] })}
      knop={t("pensionKlant.opvang.afmelden.bevestig")}
      kan
      gevaar
      bezig={bezig}
      fout={fout}
      ctx={ctx}
      onSluit={onSluit}
      onOpslaan={() =>
        void doe(`${basis}/dagen/${dag.id}/afmelden`, "post", { reden: reden.trim() === "" ? null : reden.trim() })
      }
    >
      <p className="pk-ov__uitleg">{t("pensionKlant.opvang.afmelden.uitleg")}</p>
      <label className="pk-veld">
        <span>{t("pensionKlant.opvang.afmelden.reden")}</span>
        <input type="text" maxLength={200} value={reden} onChange={(e) => setReden(e.target.value)} />
      </label>
    </Formulier>
  );
}

function VerwijderVenster({ basis, dag, ctx, onSluit, onKlaar }: VensterProps & { dag: OpvangDag }) {
  const { t, locale } = ctx;
  const { doe, bezig, fout } = useOpslaan(ctx, onKlaar);
  return (
    <Formulier
      titel={t("pensionKlant.opvang.verwijderen.titel", { args: [weekdagDatum(dag.datum, locale)] })}
      knop={t("pensionKlant.opvang.verwijderen.bevestig")}
      kan
      gevaar
      bezig={bezig}
      fout={fout}
      ctx={ctx}
      onSluit={onSluit}
      onOpslaan={() => void doe(`${basis}/dagen/${dag.id}`, "delete")}
    >
      <p className="pk-ov__uitleg">{t("pensionKlant.opvang.verwijderen.uitleg")}</p>
    </Formulier>
  );
}

function VasteDagenVenster({
  basis,
  opvang,
  dieren,
  ctx,
  onSluit,
  onKlaar,
}: VensterProps & { opvang: PensionDagopvangOverzicht; dieren: PensionDier[] }) {
  const { t, locale, vandaag } = ctx;
  const [plan, setPlan] = useState<Record<number, Weekdag[]>>(() =>
    Object.fromEntries(opvang.dieren.map((d) => [d.id, d.dagen])),
  );
  // De periode die wordt ingepland. Zo gevraagd: je kiest hem hier, en die periode wordt geboekt of
  // bijgewerkt. Standaard vanaf vandaag tot het einde van wat de kalender laat zien; wat al voorbij is
  // laat de api met rust. Leeg maken mag: dan verandert alleen het weekpatroon.
  const [van, setVan] = useState(() => (opvang.van > vandaag ? opvang.van : vandaag));
  const [tot, setTot] = useState(opvang.tot);
  const { doe, bezig, fout } = useOpslaan(ctx, onKlaar);
  const erop = new Set(opvang.dieren.map((d) => d.id));
  const lijst = kandidaten(opvang, dieren);
  // Een dier dat nog niet op het contract staat, komt er pas bij met minstens één dag.
  const teBewaren = lijst.filter((k) => erop.has(k.id) || (plan[k.id]?.length ?? 0) > 0);
  const wissel = (id: number, dag: Weekdag) =>
    setPlan((p) => {
      const nu = p[id] ?? [];
      return { ...p, [id]: nu.includes(dag) ? nu.filter((x) => x !== dag) : WEEK.filter((w) => w === dag || nu.includes(w)) };
    });
  const halveperiode = (van === "") !== (tot === "");
  const omgekeerd = van !== "" && tot !== "" && tot < van;
  return (
    <Formulier
      titel={t("pensionKlant.opvang.vast.titel")}
      knop={t("pensionKlant.opvang.opslaan")}
      kan={teBewaren.length > 0 && !halveperiode && !omgekeerd}
      bezig={bezig}
      fout={fout}
      ctx={ctx}
      onSluit={onSluit}
      onOpslaan={() =>
        void doe(`${basis}/contracten/${opvang.contract!.id}/vaste-dagen`, "put", {
          dieren: teBewaren.map((k) => ({ dierId: k.id, dagen: plan[k.id] ?? [] })),
          van: van === "" ? null : van,
          tot: tot === "" ? null : tot,
        })
      }
    >
      <p className="pk-ov__uitleg">{t("pensionKlant.opvang.vast.uitleg")}</p>

      <section className="pk-dv__vak" aria-label={t("pensionKlant.opvang.vast.periode")}>
        <h4 className="pk-dv__kop">
          <Rondje icoon={CalendarRange} tint="groen" grootte="kop" />
          {t("pensionKlant.opvang.vast.periode")}
        </h4>
        <div className="pk-ov__twee">
          <label className="pk-veld">
            <span>{t("pensionKlant.opvang.vast.van")}</span>
            <DateField value={van} onChange={(e) => setVan(e.target.value)} />
          </label>
          <label className="pk-veld">
            <span>{t("pensionKlant.opvang.vast.tot")}</span>
            <DateField value={tot} onChange={(e) => setTot(e.target.value)} min={van || undefined} />
          </label>
        </div>
        <p className="pk-voetnoot">
          {omgekeerd
            ? t("pensionKlant.opvang.vast.periodeOmgekeerd")
            : halveperiode
              ? t("pensionKlant.opvang.vast.periodeHalf")
              : van === "" && tot === ""
                ? t("pensionKlant.opvang.vast.periodeLeeg")
                : t("pensionKlant.opvang.vast.periodeUitleg")}
        </p>
      </section>

      <div className="pk-ov__vasttabel">
        {lijst.map((k) => (
          <div key={k.id} className="pk-ov__vastrij" role="group" aria-label={k.naam ?? String(k.id)}>
            <span className="pk-ov__vastnaam">{k.naam ?? LEEG}</span>
            <span className="pk-ov__weekknoppen">
              {WEEK.map((w, i) => (
                <button
                  key={w}
                  type="button"
                  className="pk-ov__weekknop"
                  aria-pressed={(plan[k.id] ?? []).includes(w)}
                  aria-label={weekdagLang(i, locale)}
                  onClick={() => wissel(k.id, w)}
                >
                  {weekdagKort(i, locale)}
                </button>
              ))}
            </span>
          </div>
        ))}
      </div>
    </Formulier>
  );
}

function AfrekeningVenster({
  basis,
  opvang,
  wijze: eerste,
  ctx,
  onSluit,
  onKlaar,
}: VensterProps & { opvang: PensionDagopvangOverzicht; wijze: Afrekenwijze }) {
  const { t, locale } = ctx;
  const c = opvang.contract!;
  const a = opvang.afrekening;
  const { abonnementen, prijzen } = opvang.opties;
  const [wijze, setWijze] = useState<Afrekenwijze>(eerste);
  const [abonnement, setAbonnement] = useState(
    a?.maandcontract.abonnementId != null ? String(a.maandcontract.abonnementId) : "",
  );
  const [prijs, setPrijs] = useState(a?.periode ? String(a.periode.prijsCategorieId) : "");
  const [brengen, setBrengen] = useState(tijd(c.brengen) ?? "");
  const [halen, setHalen] = useState(tijd(c.halen) ?? "");
  const { doe, bezig, fout } = useOpslaan(ctx, onKlaar);
  const tijdFout = brengen !== "" && halen !== "" && halen <= brengen;
  const kan = !tijdFout && (wijze !== "MAANDCONTRACT" || abonnement !== "");

  return (
    <Formulier
      titel={t("pensionKlant.opvang.afrekenwijze")}
      knop={t("pensionKlant.opvang.opslaan")}
      kan={kan}
      bezig={bezig}
      fout={fout}
      ctx={ctx}
      onSluit={onSluit}
      onOpslaan={() =>
        void doe(`${basis}/contracten/${c.id}/afrekening`, "put", {
          afrekenwijze: wijze,
          abonnementId: wijze === "MAANDCONTRACT" ? Number(abonnement) : null,
          prijsCategorieId: wijze === "RITTENKAART" || prijs === "" ? null : Number(prijs),
          brengen: brengen === "" ? null : brengen,
          halen: halen === "" ? null : halen,
        })
      }
    >
      <fieldset className="pk-ov__groep">
        <legend>{t("pensionKlant.opvang.afrekening.hoe")}</legend>
        {WIJZEN.map((w) => (
          <label key={w} className="pk-ov__keuze">
            <input type="radio" name="afrekenwijze" checked={wijze === w} onChange={() => setWijze(w)} />
            <span>
              <strong>{t(`pensionKlant.opvang.wijze.${w}`)}</strong>
              <span>{t(`pensionKlant.opvang.wijze.${w}.uitleg`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {wijze === "MAANDCONTRACT" &&
        (abonnementen.length === 0 ? (
          <p className="pk-voetnoot" data-soort="fout">
            {t("pensionKlant.opvang.afrekening.geenAbonnementen")}
          </p>
        ) : (
          <label className="pk-veld">
            <span>{t("pensionKlant.opvang.afrekening.abonnement")}</span>
            <select value={abonnement} onChange={(e) => setAbonnement(e.target.value)}>
              <option value="">{t("pensionKlant.opvang.afrekening.kies")}</option>
              {abonnementen.map((o) => (
                <option key={o.id} value={o.id}>
                  {`${o.naam} — ${t("pensionKlant.opvang.afrekening.perWeek", { args: [o.dagenPerWeek] })} — ${formatEuro(o.prijsPerMaand, locale)}`}
                </option>
              ))}
            </select>
          </label>
        ))}
      {wijze !== "RITTENKAART" && (
        <label className="pk-veld">
          <span>{t("pensionKlant.opvang.afrekening.dagprijs")}</span>
          <select value={prijs} onChange={(e) => setPrijs(e.target.value)}>
            <option value="">{t("pensionKlant.opvang.afrekening.standaardPrijs")}</option>
            {prijzen.map((p) => (
              <option key={p.id} value={p.id}>
                {p.dagprijs != null ? `${p.naam} — ${formatEuro(p.dagprijs, locale)}` : p.naam}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="pk-ov__twee">
        <label className="pk-veld">
          <span>{t("pensionKlant.opvang.afrekening.brengen")}</span>
          <input type="time" value={brengen} onChange={(e) => setBrengen(e.target.value)} />
        </label>
        <label className="pk-veld">
          <span>{t("pensionKlant.opvang.afrekening.halen")}</span>
          <input type="time" value={halen} onChange={(e) => setHalen(e.target.value)} />
        </label>
      </div>
      {tijdFout && (
        <p className="pk-voetnoot" data-soort="fout">
          {t("pensionKlant.opvang.fout.tijden")}
        </p>
      )}
    </Formulier>
  );
}

function RittenkaartVenster({
  basis,
  opvang,
  ctx,
  onSluit,
  onKlaar,
}: VensterProps & { opvang: PensionDagopvangOverzicht }) {
  const { t, locale } = ctx;
  const kaarten = opvang.opties.rittenkaarten;
  const [keuze, setKeuze] = useState<number | null>(kaarten[0]?.id ?? null);
  const { doe, bezig, fout } = useOpslaan(ctx, onKlaar);
  return (
    <Formulier
      titel={t("pensionKlant.opvang.rittenkaartVerkopen")}
      knop={t("pensionKlant.opvang.rittenkaart.verkoop")}
      kan={keuze != null}
      bezig={bezig}
      fout={fout}
      ctx={ctx}
      onSluit={onSluit}
      onOpslaan={() => void doe(`${basis}/rittenkaarten`, "post", { rittenkaartId: keuze })}
    >
      {kaarten.length === 0 ? (
        <p className="pk-ov__uitleg">{t("pensionKlant.opvang.rittenkaart.geen")}</p>
      ) : (
        <fieldset className="pk-ov__groep">
          <legend>{t("pensionKlant.opvang.rittenkaart.welke")}</legend>
          {kaarten.map((k) => (
            <label key={k.id} className="pk-ov__keuze">
              <input type="radio" name="rittenkaart" checked={keuze === k.id} onChange={() => setKeuze(k.id)} />
              <span>
                <strong>{k.naam}</strong>
                <span>
                  {[
                    t("pensionKlant.opvang.rittenkaart.ritten", { args: [k.aantalRitten] }),
                    formatEuro(k.prijs, locale),
                    k.geldigheidMaanden ? t("pensionKlant.opvang.rittenkaart.geldig", { args: [k.geldigheidMaanden] }) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </Formulier>
  );
}
